<script>
import * as m from '@app/paraglide/messages.js';
import { onMount } from "svelte";

import { projectState } from "@app/store/projectStore.svelte.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { lockState } from "@app/store/lockStore.svelte.js";
import { playerState } from "@app/store/playerStore.svelte.js";
import { editorState, filterState, resetFilters, hasActiveFilters } from "@app/store/editorStore.svelte.js";
import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
import { showModal } from "@app/store/modalStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import tcLib from "@cclib/lib/timecode.js";
import sortByTime from "@cclib/functions/eventGroups/orderByTime.js";

let { filteredEventCount = 0, totalEventCount = 0 } = $props();

let selectedEvent = $derived(eventGroupState[projectState.selected]?.selected[0]+1 || 1);

function selectEvent() {    
    if (selectedEvent <= eventGroupState[projectState.selected].events.length && selectedEvent > 0) {
        eventGroupState[projectState.selected].selected = [
            Math.max(0, selectedEvent - 1)
        ];

        const selectedIndex = eventGroupState[projectState.selected].selected[0];
        
        // Dispatch custom event to scroll to the selected event
        window.dispatchEvent(new CustomEvent('scrollToVirtualListItem', {
            detail: { index: selectedIndex }
        }));

        if (lockState.video && player){
            player.currentTime = eventGroupState[projectState.selected].events[selectedIndex].start;
        }
    }
}

function updateSelectedEvent() {
    selectedEvent = eventGroupState[projectState.selected].selected[0] + 1;
}

function updateEditModeStatus(ev) {
    try {
        ev.preventDefault();
        ev.stopPropagation();
        let newState = !editorState.editing;

        setTimeout(() => {
            editorState.editing = newState;
            if (editorState.editing && window.quillEditor) {
                window.quillEditor.setSelection(window.quillEditor.getLength(), 0);
            } else if (window.quillEditor) {
                window.quillEditor.blur();
            }
        }, 50);
    } catch (err) {
        console.log("Toggle Edit Mode Shortcut Error: ", err.message);
    }
}

function toggleFilters() {
    filterState.panelVisible = !filterState.panelVisible;
}

function orderSelectedEventGroupByTime() {
    eventGroupState[projectState.selected] = sortByTime(eventGroupState[projectState.selected]);
    eventGroupState[projectState.selected].selected = [];
    insertHistoryAction({
        name: "ordering",
        eventGroup: projectState.selected,
        snapshots: [{
            store: "eventGroupState",
            value: JSON.stringify(eventGroupState)
        }]
    });

    toast.push(m.events_ordered_by_time(), {classes: ["toast-success"]});
}

onMount(() => {
    if (eventGroupState[projectState.selected].selected.length > 0) {
        updateSelectedEvent();
    }
});
</script>

<svelte:head>
    <title>{projectState.name}</title>
</svelte:head>
<p class="text-dark text-end m-0 pt-1 pe-2 small">
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <i
        class="bi bi-circle-fill float-start mx-2 {editorState.editing
            ? 'text-primary'
            : 'text-secondary'}"
        title={m.editor_edit_mode_status()}
        onclick={(ev) => updateEditModeStatus(ev)}></i>
    
    <!-- Filter Toggle Button -->
    <button 
        class="btn btn-sm py-0 px-1 border-0 float-start {filterState.panelVisible ? 'btn-outline-primary' : hasActiveFilters() ? 'btn-primary' : 'btn-outline-secondary'}"
        onclick={toggleFilters}
        title={m.editor_toggle_filters()}>
        <i class="bi bi-funnel{filterState.panelVisible || hasActiveFilters() ? '-fill' : ''}"></i>
    </button>
    <button
        class="btn btn-sm py-0 px-1 border-0 float-start btn-outline-secondary"
        onclick={orderSelectedEventGroupByTime}
        title={m.events_ordered_by_time()}>
        <i class="bi bi-sort-numeric-down"></i>
    </button>

    <!-- Filter Status (shown when filters are active) -->
    {#if hasActiveFilters()}
        <span class="float-start ms-2 text-primary small" title={m.editor_filter_active()}>
            {filteredEventCount}/{totalEventCount}
        </span>
        <button 
            class="btn btn-sm py-0 px-1 border-0 float-start text-danger"
            onclick={() => resetFilters()}
            title={m.editor_clear_all_filters()}>
            <i class="bi bi-x-circle-fill"></i>
        </button>
    {/if}

    <a
        class="text-dark"
        aria-label={m.editor_show_project_info()}
        href="#!/"
        onclick={() => {showModal("projectInfo"); console.log($state.snapshot(projectState))}}>
        <i class="bi bi-info-circle"></i>
    </a>

    {projectState.name}
    <i class="bi bi-film ms-2" title={m.editor_project_frame_rate()}></i>
    {projectState.frameRate}
    <i class="bi bi-card-list ms-2" title={m.editor_number_of_events()}></i>

    <input
        id="EventNavSelectInput"
        aria-label={m.editor_select_event_number()}
        type="number"
        step="1"
        min="1"
        max={eventGroupState[projectState.selected].events.length}
        bind:value={selectedEvent}
        onchange={() => selectEvent()}
        onfocus={(e) => {
            e.target.select();
        }} />
    /
    {eventGroupState[projectState.selected].events.length}

    <i class="bi bi-clock ms-2" title={m.editor_duration()}></i>
    {playerState.duration
        ? tcLib.secToTc(
              playerState.duration,
              projectState.frameRate,
              projectState.dropFrame,
          )
        : ""}
</p>

<style>
#EventNavSelectInput {
    background: none;
    border: none;
    width: 8.2ex;
    text-align: right;
}
</style>
